import { useState } from "react";
import { ApiError } from "@shared/api-client";
import { track } from "@/lib/analytics";
import { createInquirySubmissionKey, postInquiry } from "./submit-inquiry";
import type { SubmitState } from "./types";

/** The shared network-failure copy every submission path renders. */
export const SUBMIT_NETWORK_ERROR_MESSAGE =
	"Network error. Please check your connection and try again.";

export type InquirySubmitPath = "full_brief" | "quick_enquiry" | "dry_hire";

interface UseInquirySubmitOptions {
	profileKey: string;
	path: InquirySubmitPath;
	/** Builds the wire body — one of submit-inquiry.ts's builders, called
	 *  with the hook's stable idempotency key so a retried click replays the
	 *  same submission. */
	build: (submissionKey: string) => FormData;
}

export interface InquirySubmitLifecycle {
	state: SubmitState;
	isSubmitting: boolean;
	/** The error-state message, or null — for the in-form error line. */
	submitError: string | null;
	/** Run the submit lifecycle. Callers validate BEFORE calling this —
	 *  field validation (and its analytics) stays per-form. */
	submit: () => Promise<void>;
}

/**
 * The one submit lifecycle for every inquiry submission path (#240
 * candidate A10): double-submit guard → POST → success/failure analytics →
 * ApiError message or the shared network-failure copy → scroll to top on
 * success. The wire formats stay in submit-inquiry.ts; the forms keep only
 * their own field validation and rendering.
 */
export function useInquirySubmit({
	profileKey,
	path,
	build,
}: UseInquirySubmitOptions): InquirySubmitLifecycle {
	const [state, setState] = useState<SubmitState>({ status: "idle" });
	// Stable for the lifetime of one form draft; replaying the same POST is safe.
	const [submissionKey] = useState(createInquirySubmissionKey);

	const submit = async (): Promise<void> => {
		if (state.status === "submitting") return;
		setState({ status: "submitting" });
		try {
			const result = await postInquiry(build(submissionKey));
			track("inquiry_submitted", { profileKey, path, submissionKey });
			setState({ status: "success", statusToken: result.statusToken });
			window.scrollTo({ top: 0, behavior: "instant" });
		} catch (error) {
			track("inquiry_submit_failed", { profileKey, path });
			setState({
				status: "error",
				message:
					error instanceof ApiError
						? error.message
						: SUBMIT_NETWORK_ERROR_MESSAGE,
			});
		}
	};

	return {
		state,
		isSubmitting: state.status === "submitting",
		submitError: state.status === "error" ? state.message : null,
		submit,
	};
}
